import { CHANGE_CITY_LOGIC_PATHS, PATHS } from '@/constants/routes';
import { useAuth, useDialog, useGuest, useMovieFinder, useSnack } from '@/logic/state';

import { ChangeEvent } from 'react';
import shallow from 'zustand/shallow';
import { useRouter } from 'next/router';
import { useUpdateAuthUserCity } from '@/logic/services';
import { useUpdatePath } from './use-update-path';

// this hook is used to update (Auth and guest) users selected city

type CloseModals = () => void;

interface UpdateUserSelectedCityModel {
  update: (e: ChangeEvent<HTMLInputElement>) => void;
  isLoading: boolean;
}

type UseUpdateUserSelectedCityFunc = (
  closeModals: CloseModals
) => UpdateUserSelectedCityModel;

export const useUpdateUserSelectedCity: UseUpdateUserSelectedCityFunc = closeModals => {
  const router = useRouter();

  const setDialog = useDialog(s => s.setDialog, shallow);
  const setSnack = useSnack(s => s.setSnack, shallow);
  const setGuest = useGuest(s => s.setGuest, shallow);
  const [user, setUser] = useAuth(s => [s.user, s.setUser], shallow);
  const updateAuthUserCity = useUpdateAuthUserCity();
  const { updateChangeCityPath } = useUpdatePath();
  const [selectedFilters] = useMovieFinder(s => [s.selectedFilters], shallow);

  const submitSelectedCity = (selectedCityId: number) => {
    updateAuthUserCity.safeMutateAsync(
      {
        data: {
          cityId: selectedCityId,
        },
      },
      {
        onSuccess: data => {
          const { language: _, ...restUserData } = data.user;
          setUser(prevState => ({
            ...prevState,
            ...restUserData,
            preferredCityId: data.user.preferredCityId,
          }));
          setSnack({
            isOpen: true,
            message: 'cities.update.success',
            type: 'success',
          });
          closeModals();
        },
      }
    );
  };

  const changeCity = (e: ChangeEvent<HTMLInputElement>) => {
    if (user) {
      submitSelectedCity(Number(e.target.value));
    } else {
      setGuest(prevState => ({
        ...prevState,
        preferredCityId: Number(e.target.value),
      }));
      setSnack({
        isOpen: true,
        message: 'cities.update.success',
        type: 'success',
      });
      closeModals();
    }
  };

  const checkIfCurrentPathIsFinder = (): string => {
    if (router.pathname === PATHS.FINDER) {
      if (!!selectedFilters.length) {
        return 'movie.finder.city.will.change.and.filters.will.be.cleared';
      } else {
        return 'ignore';
      }
    }
  };

  const handleChangeCityConfirmDialog = (e: ChangeEvent<HTMLInputElement>): void => {
    const dialogDescription: string | 'ignore' = checkIfCurrentPathIsFinder();
    if (dialogDescription === 'ignore') {
      changeCity(e);
      return;
    }
    setDialog({
      wrapper: true,
      isChangeCityConfirmDialog: true,
      changeCityConfirmDialogPayload: {
        description: dialogDescription,
        handleOkayClick: () => {
          setDialog({});
          updateChangeCityPath();
          changeCity(e);
        },
      },
    });
  };
  const updateUserSelectedCity = (e: ChangeEvent<HTMLInputElement>) => {
    CHANGE_CITY_LOGIC_PATHS.includes(router.pathname as PATHS)
      ? handleChangeCityConfirmDialog(e)
      : changeCity(e);
  };

  return {
    update: updateUserSelectedCity,
    isLoading: updateAuthUserCity.isLoading,
  };
};
